/**
 * repositorySelectionMapping.js — historia 170 (Repositorio Documental), J4b. Pure mapping from
 * `useRepositorySelection()`'s `repositorySelection` (`{windowReferenceId, windowCode,
 * selectionReferenceId, selectionCode}`, or `{}` — never `null`/`undefined`, CLAUDE.md) to the two
 * `SmartMateriaUITable.preselectedRecord` shapes ({ field, value, searchText }) a destination window
 * needs: its OWN (parent) table, and a NESTED child table when the row has a child selection
 * (S103 — "Ventanas anidadas", `03-dominio.md`).
 *
 * `field` is always `'id'` here: every window's `SmartMateriaUITable` row carries the database id
 * under that key (verified per-window against each `dataFetch` DTO). `value` defaults to that id
 * coerced to a Number (it always arrives as a string from the URL/`state.repositorySelection`) — the
 * `RecordIdentityKind` for almost every window (`03-dominio.md`). Two topics are the documented
 * exception: `QUALITY_INCIDENCE`/`QUALITY_INCIDENCE_ACTION` are `RecordIdentityKind.UUID` — their
 * callers (`Quality.jsx`) pass `{ numeric: false }` to keep the id as text (`Number(uuid)` is `NaN`,
 * which would never match any real row).
 *
 * Returns `null` (not `{}`) for "nothing to preselect", deliberately: `SmartMateriaUITable`'s own
 * `preselectedRecord` prop is `null` when absent and its guard is `!preselectedRecord` — passing `{}`
 * instead (truthy in JS) would NOT be treated as absent and would crash
 * (`getObjectPropertyFromString(row, undefined)`, `field` missing). This file lives under `generic/`,
 * exempt from CLAUDE.md's "no null/undefined in business logic" — matching the exact sentinel the
 * component it feeds already expects is the correct, safe choice here.
 */
export const parentPreselectedRecordFrom = (repositorySelection, { numeric = true } = {}) => {
  if (!repositorySelection || !repositorySelection.windowReferenceId) return null
  return {
    field: 'id',
    value: numeric ? Number(repositorySelection.windowReferenceId) : repositorySelection.windowReferenceId,
    searchText: repositorySelection.windowCode
  }
}

export const childPreselectedRecordFrom = (repositorySelection, { numeric = true } = {}) => {
  if (!repositorySelection || !repositorySelection.selectionReferenceId) return null
  return {
    field: 'id',
    value: numeric ? Number(repositorySelection.selectionReferenceId) : repositorySelection.selectionReferenceId,
    searchText: repositorySelection.selectionCode
  }
}

/**
 * S103 — nested windows (`ORDER` -> `LineOrder.jsx`, `PART_REFERENCE` -> `Part.jsx`, etc.) render one
 * child table PER parent row (one `LineOrder` per order, one `Part.jsx` dialog content per
 * reference...). Only the row that IS the window's own preselection should ever receive the child's
 * `preselectedRecord` — every other row (nothing to do with this navigation) must get `null`, even
 * while a `repositorySelection` is present for a sibling row.
 */
export const childPreselectedRecordForRow = (repositorySelection, rowId, { numeric = true } = {}) => {
  if (!repositorySelection) return null
  const windowId = numeric ? Number(repositorySelection.windowReferenceId) : repositorySelection.windowReferenceId
  if (windowId !== rowId) return null
  return childPreselectedRecordFrom(repositorySelection, { numeric })
}
